import * as React from "react";
import { Button as AriaButton, type ButtonProps as AriaButtonProps } from "react-aria-components";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground pressed:bg-primary/90 hovered:bg-primary/90",
        destructive:
          "bg-destructive text-white pressed:bg-destructive/90 hovered:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
        outline:
          "border bg-background text-foreground hovered:bg-accent hovered:text-accent-foreground dark:bg-input/30 dark:border-input dark:hovered:bg-input/50",
        secondary:
          "bg-secondary text-secondary-foreground hovered:bg-secondary/80",
        ghost:
          "hovered:bg-accent hovered:text-accent-foreground dark:hovered:bg-accent/50",
        link: "text-link underline-offset-4 hovered:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9 rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends Omit<AriaButtonProps, "className" | "children">,
    VariantProps<typeof buttonVariants> {
  className?: string;
  /** Radix-compat alias for isDisabled */
  disabled?: boolean;
  /** Radix-compat: render child as the button element */
  asChild?: boolean;
  children?: React.ReactNode;
}

function Button({
  className,
  variant,
  size,
  disabled,
  asChild,
  children,
  ...props
}: ButtonProps) {
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
      className: cn(buttonVariants({ variant, size, className }), (children.props as { className?: string })?.className),
      "data-slot": "button",
    });
  }

  return (
    <AriaButton
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      isDisabled={disabled ?? props.isDisabled}
      {...props}
    >
      {children}
    </AriaButton>
  );
}

export { Button, buttonVariants };
